import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Play, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './GameHub.css';

const GameHub = () => {
  const navigate = useNavigate();
  const [savedGames, setSavedGames] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadSavedGames();
  }, []);

  const loadSavedGames = () => {
    try {
      // Load from localStorage (same key as cheese-chase.html uses)
      const data = localStorage.getItem('cheeseChaseQuizDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      
      console.log('📂 Loaded quizzes from localStorage:', quizzes);
      
      // Transform to match expected format
      const games = quizzes.map(quiz => ({
        id: quiz.id,
        title: quiz.title,
        dateCreated: quiz.createdAt || new Date().toISOString(),
        progress: {
          currentQuestion: 0,
          totalQuestions: quiz.questions?.length || 1,
          score: 0
        },
        quizData: quiz // Store full quiz data for playing
      }));
      
      setSavedGames(games);
    } catch (error) {
      console.error('Error loading saved games:', error);
      toast.error('Failed to load saved games');
    }
  };

  const createNewGame = () => {
    navigate('/cheese-chase-game');
  };

  const continueGame = (gameId) => {
    navigate(`/cheese-chase-game?gameId=${gameId}`);
  };

  const deleteGame = (gameId) => {
    try {
      // Delete from localStorage
      const data = localStorage.getItem('cheeseChaseQuizDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      const filteredQuizzes = quizzes.filter(q => q.id !== gameId);
      localStorage.setItem('cheeseChaseQuizDatabase', JSON.stringify(filteredQuizzes));
      
      console.log('🗑️ Quiz deleted:', gameId);
      
      loadSavedGames();
      setShowDeleteConfirm(null);
      toast.success('Quiz deleted successfully');
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error('Failed to delete quiz');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="game-hub-page">
      <div className="game-hub-container">
        {/* Header */}
        <div className="hub-header">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
            variant="ghost"
          >
            <ArrowLeft size={20} />
            Back
          </Button>
          <div className="hub-title">
            <h1>🧀 Cheese Chase Hub</h1>
            <p>Your saved quiz games</p>
          </div>
          <Button 
            onClick={createNewGame} 
            className="create-button"
          >
            <Plus size={20} />
            Create New Game
          </Button>
        </div>

        {/* Games List */}
        <div className="games-grid">
          {savedGames.length === 0 ? (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="empty-icon">🎮</div>
              <h3>No saved games yet</h3>
              <p>Create your first Cheese Chase quiz game!</p>
              <Button onClick={createNewGame} className="empty-create-btn">
                <Plus size={20} />
                Create New Game
              </Button>
            </motion.div>
          ) : (
            savedGames.map((game, index) => (
              <motion.div
                key={game.id}
                className="game-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="game-card-header">
                  <div className="game-icon">🧀</div>
                  <div className="game-info">
                    <h3>{game.title || 'Untitled Game'}</h3>
                    <div className="game-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {formatDate(game.dateCreated)}
                      </span>
                      <span className="meta-item">
                        <Clock size={14} />
                        {formatTime(game.dateCreated)}
                      </span>
                    </div>
                  </div>
                </div>

                {game.progress && (
                  <div className="game-progress">
                    <div className="progress-label">
                      Progress: {game.progress.currentQuestion || 0} / {game.progress.totalQuestions || 0}
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${((game.progress.currentQuestion || 0) / (game.progress.totalQuestions || 1)) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="game-card-actions">
                  <Button 
                    onClick={() => continueGame(game.id)} 
                    className="continue-btn"
                  >
                    <Play size={18} />
                    {game.progress?.currentQuestion > 0 ? 'Continue' : 'Start'}
                  </Button>
                  <Button 
                    onClick={() => setShowDeleteConfirm(game.id)} 
                    className="delete-btn"
                    variant="ghost"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="delete-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Game?</h3>
              <p>This action cannot be undone. All progress will be lost.</p>
              <div className="delete-actions">
                <Button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteGame(showDeleteConfirm)} 
                  className="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameHub;
